class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.inputType = this.querySelector('[data-predictive-search-type]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.hostname = window.location.hostname;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.js-search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  async onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.chooseSearchEngine()) {
      this.enableGQLSearchResults();
      this.searchResults = await this.getGQLSearchResults(searchTerm);
      this.renderSearchResults();
    };
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  async onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      if (this.chooseSearchEngine()) {
        this.enableGQLSearchResults();
        this.searchResults = await this.getGQLSearchResults(searchTerm);
        this.renderSearchResults();
        return;
      }
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  async getGQLSearchResults(query) {
    const STOREFRONT_API_URL = `${location.origin}/api/2025-01/graphql.json`;
    const STOREFRONT_API_TOKEN = '1bcf6058069ec6f519e5beaf8911299a';

    const graphqlQuery = `
      query PredictiveSearchWithProductMeta($query: String!, $limit: Int = 10) {
        predictiveSearch(
          query: $query
          limit: $limit
          types: [PRODUCT]
          searchableFields: [TITLE]
        ) {
          products {
            title
            vendor
            tags
            onlineStoreUrl
            priceRange {
              minVariantPrice {
                amount
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
              }
            }
            category {
              name
            }
            media (first: 1) {
              nodes {
                alt
                previewImage {
                  url
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_API_TOKEN,
      },
      body: JSON.stringify({
        query: graphqlQuery,
        variables: { query, limit: 10 },
      }),
    });

    const result = await response.json();
    this.cachedResults[query] = result;
    return result.data.predictiveSearch.products;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.js-predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults() {
    const currentCategory = this.getCurrentCategory();
    
    this.filteredProducts = this.searchResults.filter(result => {
      if (!result.category || !result.category.name) return false;
      return result.category.name === currentCategory;
    });

    const resultsHTML = this.filteredProducts.map((product, index) => {
      return this.createProductHTML(product, index);
    }).join('');

    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__results-list">
        <ul role="listbox" id="predictive-search-results-list">
          ${resultsHTML}
        </ul>
      </div>
    `;

    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  getCurrentCategory() {    
    if (this.hostname.includes('baby')) {
      return 'Bebés y niños pequeños';
    } else if (this.hostname.includes('pet')) {
      return 'Productos para mascotas y animales';
    } else {
      return 'Salud y belleza';
    }
  }

  createProductHTML(product, index) {
    const imageUrl = product.media?.nodes?.[0]?.previewImage?.url || '';
    const imageAlt = product.media?.nodes?.[0]?.alt || product.title;
    const isOutOfStock = product.tags?.includes('agotado') || product.tags?.includes('sold-out');
    
    const price = product.priceRange ? parseFloat(product.priceRange.minVariantPrice.amount) : null;
    const compareAtPrice = product.compareAtPriceRange ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount) : null;
    
    let discountPercentage = null;
    if (price && compareAtPrice && compareAtPrice > price) {
      discountPercentage = Math.floor(((compareAtPrice - price) / compareAtPrice) * 100);
    }

    return `
      <li id="predictive-search-option-${index}" 
          role="option" 
          aria-selected="false" 
          class="predictive-search__list-item">
        <a href="${product.onlineStoreUrl}" class="predictive-search__item">
          <div class="predictive-search__item-content">
            ${isOutOfStock ? '<span class="predictive-search__badge predictive-search__badge--sold-out">AGOTADO</span>' : ''}
            ${discountPercentage ? `<span class="predictive-search__badge predictive-search__badge--discount">${discountPercentage}% OFF</span>` : ''}
            <div class="predictive-search__item-image">
              <img src="${imageUrl}" alt="${imageAlt}" loading="lazy" />
            </div>
            <div class="predictive-search__item-details">
              <div class="predictive-search__item-title">${product.title}</div>
              <div class="predictive-search__item-vendor">${product.vendor || ''}</div>
              <div class="predictive-search__item-prices">
                ${price ? `<div class="predictive-search__item-price">$${this.formatPrice(price)}</div>` : ''}
                ${compareAtPrice ? `<div class="predictive-search__item-compare-at-price">$${this.formatPrice(compareAtPrice)}</div>` : ''}
              </div>
            </div>
          </div>
        </a>
      </li>
    `;
  }

  chooseSearchEngine() {
    if(this.hostname === '127.0.0.1' || this.hostname.includes('baby') || this.hostname.includes('pet')) {
      return true
    }

    return false; 
  }

  enableGQLSearchResults() {
    this.predictiveSearchResults.classList.remove('predictive-search');
  }

  formatPrice(price) {
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
